import React, { useContext, useCallback, useEffect, useMemo, useState } from "react";
import { UserContext } from "../../../context/UserContext";
import { TableHead, Th, Tr, Td } from "../../Elements/DataTable";

function eur(n) {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(n || 0));
}

// Trimestre natural que contiene una fecha → {from, to} en YYYY-MM-DD
function quarterBounds(d = new Date()) {
  const q = Math.floor(d.getMonth() / 3);
  const from = new Date(d.getFullYear(), q * 3, 1);
  const to = new Date(d.getFullYear(), q * 3 + 3, 0);
  const iso = (x) => x.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to), label: `T${q + 1} ${d.getFullYear()}` };
}

export default function ImpuestosPage() {
  const [token, , , venueID] = useContext(UserContext);
  const initial = useMemo(() => quarterBounds(), []);
  const [fromDate, setFromDate] = useState(initial.from);
  const [toDate, setToDate] = useState(initial.to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    if (!token || !venueID) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `/v1/reports/vat?venue_id=${venueID}&date_from=${fromDate}&date_to=${toDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`Error ${res.status} al cargar el informe de IVA`);
      setData(await res.json());
    } catch (e) {
      setError(e.message || "Error al cargar el informe de IVA");
    } finally {
      setLoading(false);
    }
  }, [token, venueID, fromDate, toDate]);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setQuarter = (offset) => {
    const d = new Date();
    d.setMonth(d.getMonth() + offset * 3);
    const q = quarterBounds(d);
    setFromDate(q.from);
    setToDate(q.to);
  };

  const downloadCsv = (rows, name) => {
    const csv = rows.map((r) => r.map((c) => (c == null ? "" : String(c))).join(";")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const period = `${data?.period?.from || fromDate}_${data?.period?.to || toDate}`;

  // Modelo 303 por casillas (orientativo — la gestoría confirma)
  const casillas = useMemo(() => {
    if (!data) return null;
    const boxPairs = [["01", "03"], ["04", "06"], ["07", "09"]];
    const boxes = (data.by_rate || []).slice(0, 3).map((r, i) => ({
      baseBox: boxPairs[i][0], cuotaBox: boxPairs[i][1],
      rate: r.rate, base: r.base, cuota: r.vat,
    }));
    return {
      boxes,
      devengada: data.totals?.vat ?? 0,      // casilla 27
      soportadoBase: data.soportado?.base ?? 0, // casilla 28
      soportadoCuota: data.soportado?.vat ?? 0, // casilla 29 = 45
      resultado: data.a_liquidar ?? 0,         // casilla 46 / 71
    };
  }, [data]);

  // Export 1 — Modelo 303 por casillas (autoliquidación / Sede AEAT)
  const export303 = () => {
    if (!casillas) return;
    const rows = [["Casilla", "Concepto", "Importe (€)"]];
    casillas.boxes.forEach((b) => {
      rows.push([b.baseBox, `Base imponible ${b.rate}% (devengado)`, b.base]);
      rows.push([b.cuotaBox, `Cuota ${b.rate}% (devengado)`, b.cuota]);
    });
    rows.push(["27", "Total cuota devengada", casillas.devengada]);
    rows.push(["28", "Base cuotas soportadas (op. interiores corrientes)", casillas.soportadoBase]);
    rows.push(["29", "Cuota soportada deducible", casillas.soportadoCuota]);
    rows.push(["45", "Total a deducir", casillas.soportadoCuota]);
    rows.push(["46/71", "Resultado de la liquidación", casillas.resultado]);
    downloadCsv(rows, `modelo303_casillas_${period}.csv`);
  };

  // Export 2 — Libro de facturas recibidas (registro IVA soportado, para gestoría)
  const exportPurchasesBook = () => {
    if (!data?.purchases) return;
    const rows = [["Fecha", "Proveedor", "CIF/NIF", "Nº factura", "Concepto", "Base", "Tipo IVA %", "Cuota IVA", "Total", "Estado"]];
    data.purchases.forEach((p) => rows.push([
      p.paid_at, p.supplier_name, p.supplier_cif, p.invoice_number, p.concept,
      p.base, p.vat_rate, p.vat_amount, p.amount, p.status,
    ]));
    downloadCsv(rows, `libro_facturas_recibidas_${period}.csv`);
  };

  const byRate = data?.by_rate || [];
  const cat = data?.by_category || { dispensario: { base: 0, vat: 0 }, hosteleria: { base: 0, vat: 0 } };

  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-4 w-full max-w-5xl lg:max-w-none">
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">IVA / Impuestos</h1>
            <p className="text-sm text-gray-500 mt-1">Modelo 303: IVA repercutido − soportado = a liquidar, por periodo.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={export303}
              disabled={!data}
              title="Casillas del Modelo 303 para autoliquidar en la Sede de la AEAT"
              className="self-start rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              ⬇ Modelo 303 (casillas)
            </button>
            <button
              onClick={exportPurchasesBook}
              disabled={!data?.purchases?.length}
              title="Libro de facturas recibidas (registro de IVA soportado) para la gestoría"
              className="self-start rounded-md bg-white border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              ⬇ Libro de facturas
            </button>
          </div>
        </div>

        {/* Periodo */}
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end max-lg:portrait:flex-col max-lg:portrait:items-stretch">
          <div className="min-w-0 max-lg:portrait:w-full">
            <label className="block text-xs font-medium text-gray-600 mb-1">Desde</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full min-w-0 rounded border border-gray-300 px-3 py-2 text-sm max-lg:portrait:w-full"
            />
          </div>
          <div className="min-w-0 max-lg:portrait:w-full">
            <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full min-w-0 rounded border border-gray-300 px-3 py-2 text-sm max-lg:portrait:w-full"
            />
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto max-lg:portrait:grid max-lg:portrait:w-full max-lg:portrait:grid-cols-2 max-lg:portrait:gap-2">
            <button
              type="button"
              onClick={fetchData}
              className="rounded-md bg-gray-800 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900 max-lg:portrait:col-span-2 max-lg:portrait:w-full max-lg:portrait:justify-center"
            >
              Aplicar
            </button>
            <button
              type="button"
              onClick={() => setQuarter(0)}
              className="rounded border border-gray-300 px-3 py-2 text-xs font-semibold hover:bg-gray-100 max-lg:portrait:w-full max-lg:portrait:text-center"
            >
              Este trimestre
            </button>
            <button
              type="button"
              onClick={() => setQuarter(-1)}
              className="rounded border border-gray-300 px-3 py-2 text-xs font-semibold hover:bg-gray-100 max-lg:portrait:w-full max-lg:portrait:text-center"
            >
              Trimestre anterior
            </button>
          </div>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {loading && <div className="text-sm text-gray-500">Cargando…</div>}

      {data && !loading && (
        <>
          {/* Totales: repercutido − soportado = a liquidar */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Base ventas</div>
              <div className="mt-1 text-xl font-bold text-gray-800">{eur(data.totals?.base)}</div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">IVA repercutido</div>
              <div className="mt-1 text-xl font-bold text-emerald-700">{eur(data.totals?.vat)}</div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">IVA soportado</div>
              <div className="mt-1 text-xl font-bold text-blue-700">{eur(data.soportado?.vat)}</div>
              <div className="text-xs text-gray-400">base {eur(data.soportado?.base)}</div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">IVA a liquidar</div>
              <div className={`mt-1 text-xl font-bold ${Number(data.a_liquidar) < 0 ? "text-blue-700" : "text-gray-900"}`}>{eur(data.a_liquidar)}</div>
              <div className="text-xs text-gray-400">{Number(data.a_liquidar) < 0 ? "a compensar/devolver" : "a ingresar"}</div>
            </div>
          </div>
          <div className="text-xs text-gray-500">Periodo: {data.period?.from} → {data.period?.to}</div>

          {/* Por tipo de IVA */}
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 font-semibold text-gray-800">Por tipo de IVA</div>
            {/* La tarjeta lleva `overflow-hidden` por las esquinas redondeadas, así que lo
                que no cabía de la tabla quedaba RECORTADO y sin forma de alcanzarlo — ni
                arrastrando (reporte 25-08, iPad en vertical). La tabla se desplaza dentro
                de su propio contenedor. */}
            <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              {/* Compacta a propósito: con filas de 60 esta liquidación mide el
                  doble y deja de caber en la tarjeta. */}
              <TableHead>
                <Th className="px-4 py-2">Tipo</Th>
                <Th align="right" className="px-4 py-2">Base imponible</Th>
                <Th align="right" className="px-4 py-2">Cuota IVA</Th>
                <Th align="right" className="px-4 py-2">Líneas</Th>
              </TableHead>
              <tbody>
                {byRate.length === 0 && (
                  <tr><Td align="center" colSpan={4} className="px-4 py-4 text-[var(--muted-foreground)]">Sin ventas con IVA capturado en este periodo.</Td></tr>
                )}
                {byRate.map((r) => (
                  <Tr key={r.rate}>
                    <Td className="px-4 py-2 font-medium">{r.rate}%</Td>
                    <Td align="right" className="px-4 py-2">{eur(r.base)}</Td>
                    <Td align="right" className="px-4 py-2 font-semibold text-emerald-700">{eur(r.vat)}</Td>
                    <Td align="right" className="px-4 py-2 text-[var(--muted-foreground)]">{r.lines}</Td>
                  </Tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          {/* Modelo 303 por casillas */}
          {casillas && (
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <span className="font-semibold text-gray-800">Modelo 303 — casillas</span>
                <span className="text-xs text-gray-400">orientativo · confírmalo con tu gestoría</span>
              </div>
              <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <TableHead>
                  <Th className="w-24 px-4 py-2">Casilla</Th>
                  <Th className="px-4 py-2">Concepto</Th>
                  <Th align="right" className="px-4 py-2">Importe</Th>
                </TableHead>
                {/* OJO — el esmeralda y el azul siguen a fuego a propósito: no son
                    decoración, son el PAR que distingue devengado (lo que se repercute)
                    de soportado (lo que se deduce), y solo funcionan por contraste entre
                    ellos. El sistema no tiene paleta categórica: pasar el esmeralda a
                    `--success` y dejar el azul huérfano rompería la pareja. Necesita
                    decisión de diseño, no un apaño de tabla. */}
                <tbody>
                  {casillas.boxes.map((b) => (
                    <React.Fragment key={b.baseBox}>
                      <Tr>
                        <Td className="px-4 py-2 font-mono text-[var(--muted-foreground)]">{b.baseBox}</Td>
                        <Td className="px-4 py-2">Base imponible {b.rate}% (devengado)</Td>
                        <Td align="right" className="px-4 py-2">{eur(b.base)}</Td>
                      </Tr>
                      <Tr>
                        <Td className="px-4 py-2 font-mono text-[var(--muted-foreground)]">{b.cuotaBox}</Td>
                        <Td className="px-4 py-2">Cuota {b.rate}% (devengado)</Td>
                        <Td align="right" className="px-4 py-2 font-semibold text-emerald-700">{eur(b.cuota)}</Td>
                      </Tr>
                    </React.Fragment>
                  ))}
                  {/* Los subtotales no son cebra: van sobre la superficie sutil del
                      sistema porque SUMAN, no porque toque fila par. */}
                  <Tr className="bg-[var(--surface-subtle)]">
                    <Td className="px-4 py-2 font-mono text-[var(--muted-foreground)]">27</Td>
                    <Td className="px-4 py-2 font-medium">Total cuota devengada</Td>
                    <Td align="right" className="px-4 py-2 font-semibold text-emerald-700">{eur(casillas.devengada)}</Td>
                  </Tr>
                  <Tr>
                    <Td className="px-4 py-2 font-mono text-[var(--muted-foreground)]">28</Td>
                    <Td className="px-4 py-2">Base cuotas soportadas (op. interiores corrientes)</Td>
                    <Td align="right" className="px-4 py-2">{eur(casillas.soportadoBase)}</Td>
                  </Tr>
                  <Tr>
                    <Td className="px-4 py-2 font-mono text-[var(--muted-foreground)]">29</Td>
                    <Td className="px-4 py-2">Cuota soportada deducible</Td>
                    <Td align="right" className="px-4 py-2 font-semibold text-blue-700">{eur(casillas.soportadoCuota)}</Td>
                  </Tr>
                  <Tr className="bg-[var(--surface-subtle)]">
                    <Td className="px-4 py-2 font-mono text-[var(--muted-foreground)]">45</Td>
                    <Td className="px-4 py-2 font-medium">Total a deducir</Td>
                    <Td align="right" className="px-4 py-2 font-semibold text-blue-700">{eur(casillas.soportadoCuota)}</Td>
                  </Tr>
                  {/* Invertida: es la línea que se busca al abrir esta tabla. */}
                  <tr className="bg-[var(--foreground)] text-[var(--primary-foreground)]">
                    <td className="px-4 py-2.5 font-mono">46/71</td>
                    <td className="px-4 py-2.5 font-semibold">Resultado de la liquidación</td>
                    <td className="px-4 py-2.5 text-right font-bold">{eur(casillas.resultado)}</td>
                  </tr>
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* Dispensario vs hostelería */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[["Dispensario (cannabis)", cat.dispensario], ["Hostelería (comida/bebida)", cat.hosteleria]].map(([label, c]) => (
              <div key={label} className="bg-white rounded-lg shadow border border-gray-200 p-4">
                <div className="text-sm font-semibold text-gray-700">{label}</div>
                <div className="mt-2 flex justify-between text-sm"><span className="text-gray-500">Base</span><span className="font-medium">{eur(c.base)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">IVA</span><span className="font-semibold text-emerald-700">{eur(c.vat)}</span></div>
              </div>
            ))}
          </div>

          {/* Aviso líneas sin capturar */}
          {data.uncaptured?.lines > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <strong>{data.uncaptured.lines}</strong> línea(s) de venta sin IVA capturado ({eur(data.uncaptured.gross_amount)} bruto)
              no se incluyen en el cálculo. Son ventas previas a activar la captura de IVA; las nuevas ventas sí se capturan.
            </div>
          )}
        </>
      )}
    </div>
  );
}
